'use client';
import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function ServiceWorkerRegistration() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          ?.register('/sw.js', { scope: '/' })
          ?.then((registration) => {
            setInterval(() => registration?.update(), 60 * 60 * 1000);
          })
          ?.catch((err) => {
            console.warn('Service Worker registration failed:', err);
          });
      });
    }

    // Capture the beforeinstallprompt event so we can trigger it manually
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      // Only show banner if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Also check if already in standalone mode (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowBanner(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
      setInstallPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
  };

  if (!showBanner || !installPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto">
      <div className="bg-amber-950 border border-amber-700 rounded-2xl p-4 shadow-2xl flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <Download size={18} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-100">App installieren</p>
          <p className="text-xs text-amber-300/80 mt-0.5 leading-relaxed">
            Zum Startbildschirm hinzufügen — auch offline nutzbar!
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-amber-950 text-xs font-bold py-2 px-3 rounded-lg transition-colors"
            >
              Installieren
            </button>
            <button
              onClick={handleDismiss}
              className="text-xs text-amber-400 hover:text-amber-300 py-2 px-3 rounded-lg transition-colors"
            >
              Später
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-amber-500/60 hover:text-amber-400 flex-shrink-0"
          aria-label="Schließen"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
